import { TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { SORCERY_CHART_COLORS } from '../../utils/sorcery/deckMetrics';
import StatCard from './StatCard';
import { noiseBarStyle } from './ChartNoise';
import { t } from '../../localization/t';

const RARITY_COLORS = {
  Ordinary: SORCERY_CHART_COLORS.Neutral,
  Exceptional: SORCERY_CHART_COLORS.Water,
  Elite: SORCERY_CHART_COLORS.Fire,
  Unique: SORCERY_CHART_COLORS.Air,
};

function RarityBar({ label, rows, field, total, emptyNote }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="w-20 shrink-0 font-medium text-xs"
        style={{ color: total === 0 ? TEXT_MUTED : TEXT_PRIMARY }}
      >
        {label}
      </span>
      {total === 0 ? (
        <span className="flex-1 text-xs" style={{ color: TEXT_BODY, opacity: 0.7 }}>
          {emptyNote}
        </span>
      ) : (
        <>
          <div
            className="flex-1 flex h-3 rounded-full overflow-hidden min-w-0"
            style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(180,140,60,0.1)' }}
          >
            {rows.map((row) => {
              const value = row[field];
              if (value === 0) return null;
              return (
                <div
                  key={row.label}
                  style={{
                    width: `${(value / total) * 100}%`,
                    ...noiseBarStyle(`linear-gradient(180deg, ${row.fill} 0%, ${row.fill}cc 100%)`),
                  }}
                  title={t('{label}: {value}', { label: t(row.label), value })}
                />
              );
            })}
          </div>
          <span className="w-8 text-right text-xs tabular-nums shrink-0" style={{ color: TEXT_PRIMARY }}>
            {total}
          </span>
        </>
      )}
    </div>
  );
}

export function RarityCollectionCard({ metrics, className }) {
  const atlasByLabel = new Map(metrics.charts.rarityAtlas.map((row) => [row.label, row.value]));
  const rows = metrics.charts.rarity
    .map((row) => ({
      label: row.label,
      fill: RARITY_COLORS[row.label] || row.fill,
      spellbook: row.value,
      atlas: atlasByLabel.get(row.label) || 0,
    }))
    .filter((row) => row.spellbook > 0 || row.atlas > 0);

  const spellbookTotal = rows.reduce((sum, row) => sum + row.spellbook, 0);
  const atlasTotal = rows.reduce((sum, row) => sum + row.atlas, 0);

  return (
    <StatCard title={t('Rarity')} description={t('Rarity spread across the spellbook and atlas.')} className={className}>
      {rows.length === 0 ? (
        <div className="text-sm" style={{ color: TEXT_BODY, opacity: 0.7 }}>
          {t('No cards yet.')}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <RarityBar
              label={t('Spells')}
              rows={rows}
              field="spellbook"
              total={spellbookTotal}
              emptyNote={t('No spells yet.')}
            />
            <RarityBar
              label={t('Sites')}
              rows={rows}
              field="atlas"
              total={atlasTotal}
              emptyNote={t('No sites. This avatar plays without an atlas.')}
            />
          </div>
          <div className="flex flex-col gap-1 text-xs">
            <div className="flex items-center gap-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
              <span className="w-2 shrink-0" />
              <span className="flex-1" />
              <span className="w-14 text-right shrink-0">{t('Spells')}</span>
              <span className="w-14 text-right shrink-0">{t('Sites')}</span>
            </div>
            {rows.map((row) => (
              <div key={row.label} className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-sm shrink-0" style={{ background: row.fill }} />
                <span className="flex-1" style={{ color: TEXT_BODY }}>{t(row.label)}</span>
                <span
                  className="w-14 text-right tabular-nums shrink-0"
                  style={{ color: row.spellbook === 0 ? TEXT_MUTED : TEXT_PRIMARY }}
                >
                  {row.spellbook}
                </span>
                <span
                  className="w-14 text-right tabular-nums shrink-0"
                  style={{ color: row.atlas === 0 ? TEXT_MUTED : TEXT_PRIMARY }}
                >
                  {row.atlas}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </StatCard>
  );
}
